import { ElementType, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { Photos } from '@frontend/photos';
import { useContactPanelShallowStore } from '@frontend/shared';
import type { ThreadHeaderSize } from '@frontend/thread-header-context';
import { useDebouncedValue } from '@frontend/timer';
import { theme } from '@frontend/theme';
import {
  Avatar,
  Heading,
  HeadingProps,
  PolymorphicComponentPropWithoutRef,
  TextProps,
  Text,
  AvatarProps,
  NakedButton,
  styles,
  useTooltip,
} from '@frontend/design-system';

type CustomTextProps<E extends 'heading' | 'text'> = {
  type: E;
} & (E extends 'text' ? TextProps : HeadingProps);

export type ThreadTitleProps<
  E extends ElementType = 'div',
  TextType extends 'heading' | 'text' = 'heading'
> = PolymorphicComponentPropWithoutRef<
  E,
  {
    text: string;
    subtext?: string;
    personId?: string;
    secondaryPerson?: {
      personId: string;
      firstName: string;
      preferredName: string;
      lastName: string;
    };
    groupId?: string;
    variant?: ThreadHeaderSize;
    textProps?: CustomTextProps<TextType>;
    avatarProps?: AvatarProps;
    openContactPanel?: boolean;
    onBackClick?: () => void;
    onSecondaryPersonClick?: () => void;
    isCompactMode?: boolean;
    headOfHouseholdId?: string;
  }
>;

/**
 * `ThreadTitle` is a component that displays the title of the thread. This includes the `Avatar`, and title text (usually
 * the person name or phone number)
 * Its children will be rendered inside the title, after the avatar and title text. This is a useful place to put the
 * `PersonSecondaryInfo` and `AssociatedContactsButton` components.
 *
 * If the `variant` is 'vertical', its children will be displayed below the title text.
 *
 * @param text - The title text to display.
 * @param subtext (optional) - If provided, it will be displayed below the title text, and the children will always be rendered to the right of the title text.
 * @param personId (optional) - The ID of the person associated with the thread. If provided, the title text will be clickable and
 * will open the contact panel for the person.
 * @param secondaryPerson (optional) - The data of the secondary person whose avater will be displayed. Should normally
 * be used as the `primaryContact`/`Phone Owner` when the provided `personId` is different.
 * @param groupId (optional) - The ID of the group to which the person belongs.
 * @param variant (optional) - The layout of the title. Can be 'horizontal' or 'vertical'. Defaults to 'horizontal'.
 * @param textProps (optional) - The props to pass to the title text component. If the `type` is 'text', a `Text` component will be
 * used. If the `type` is 'heading', a `Heading` component will be used. Defaults to a `Heading` component.
 * @param avatarProps (optional) - The props to pass to the `Avatar` component.
 * @param openContactPanel (optional) - Whether the contact panel should be opened when the title text is clicked. Defaults to `true`.
 * @param onBackClick (optional) - A callback function that will be called when the back button is clicked. If provided, a back button
 * will be displayed before the avatar.
 * @param onSecondaryPersonClick (optional) - A callback function that will be called when the secondary person's avatar is clicked.
 * Useful to clear the context contact or align the context with the secondary person/phone owner.
 * @param headOfHouseholdId (optional) - The ID of the head of household. Used to display a badge on the avatar if the `personId` matches the `headofhouseholdId`.
 */
export const ThreadTitle = <E extends ElementType = 'div', TextType extends 'heading' | 'text' = 'heading'>({
  as,
  text,
  subtext,
  textProps,
  avatarProps,
  personId,
  secondaryPerson,
  groupId,
  openContactPanel = true,
  onBackClick,
  onSecondaryPersonClick,
  isCompactMode,
  headOfHouseholdId,
  children,
  variant = 'normal',
  ...rest
}: ThreadTitleProps<E, TextType>) => {
  const Component = as || 'div';
  const { t } = useTranslation('thread-header');

  const [hidePrimaryLabel, setHidePrimaryLabel] = useState<boolean>(false);

  const { setPersonId } = useContactPanelShallowStore('setPersonId');

  const showSecondaryAvatar = !!secondaryPerson?.personId && secondaryPerson?.personId !== personId;
  const { debouncedValue: debouncedShowSecondaryAvatar } = useDebouncedValue(showSecondaryAvatar, 300);
  const avatarSize = isCompactMode ? 'small' : 'large';

  const {
    Tooltip,
    triggerProps: tooltipTriggerProps,
    tooltipProps,
  } = useTooltip({ placement: 'bottom', hoverDelay: 300 });

  return (
    <Component
      css={[
        {
          display: 'flex',
          flexDirection: variant === 'small' ? 'column' : 'row',
          justifyContent: variant === 'small' ? 'center' : 'start',
          alignItems: 'center',
          columnGap: theme.spacing(1),
          overflowX: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      ]}
      {...rest}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          columnGap: theme.spacing(1),
          marginBottom: variant === 'small' && !isCompactMode ? theme.spacing(0.5) : 0,
          padding: '2px',
        }}
      >
        <AnimatePresence>
          {!!showSecondaryAvatar && (
            <motion.div
              key='secondary-avatar'
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginRight: theme.spacing(-0.5),
                cursor: 'pointer',
                overflow: 'hidden',
              }}
              initial={{ width: 0, opacity: 0 }}
              animate={debouncedShowSecondaryAvatar ? { width: 'auto', opacity: 1 } : { width: 0, opacity: 0 }}
              exit={{ width: 0, opacity: 0 }}
              onMouseOver={(e) => {
                e.stopPropagation();
                setHidePrimaryLabel(false);
              }}
            >
              <Photos.ContactProfilePhoto
                personId={secondaryPerson.personId}
                locationId={groupId}
                name={getFullName(secondaryPerson)}
                firstName={secondaryPerson.preferredName || secondaryPerson.firstName}
                lastName={secondaryPerson.lastName}
                size='small'
                disableHoverLabel={hidePrimaryLabel}
              >
                {headOfHouseholdId === secondaryPerson.personId && (
                  <span
                    css={[householdBadgeStyles, householdBadgeSmallStyles]}
                    onMouseOver={(e) => {
                      e.stopPropagation();
                      setHidePrimaryLabel(true);
                    }}
                    {...tooltipTriggerProps}
                  >
                    <Icon name='crown-small' size={10} />
                    <Tooltip {...tooltipProps}>{t('Head of Household')}</Tooltip>
                  </span>
                )}
              </Photos.ContactProfilePhoto>
              <NakedButton
                onClick={onSecondaryPersonClick}
                css={{ padding: theme.spacing(0.5, 0, 0, 2), width: '100%' }}
              >
                <div
                  data-testid='return-focus-to-primary-contact'
                  css={{
                    width: 16,
                    height: 8,
                    borderLeft: `1px solid ${theme.colors.neutral20}`,
                    borderBottom: `1px solid ${theme.colors.neutral20}`,
                    borderBottomLeftRadius: 6,
                  }}
                />
              </NakedButton>
            </motion.div>
          )}
          {personId ? (
            <div
              onMouseOver={(e) => {
                e.stopPropagation();
                setHidePrimaryLabel(false);
              }}
            >
              <Photos.ContactProfilePhoto
                personId={personId}
                locationId={groupId}
                name={text}
                disableClick={!openContactPanel}
                {...avatarProps}
                title={
                  showSecondaryAvatar
                    ? t('{{phoneOwnerName}} (Phone Owner)\n{{contextContactName}}', {
                        phoneOwnerName: getFullName(secondaryPerson),
                        contextContactName: text,
                      })
                    : avatarProps?.title
                }
                size={avatarSize}
                disableHoverLabel={hidePrimaryLabel}
              >
                {headOfHouseholdId === personId && (
                  <span
                    css={householdBadgeStyles}
                    onMouseOver={(e) => {
                      e.stopPropagation();
                      setHidePrimaryLabel(true);
                    }}
                    {...tooltipTriggerProps}
                  >
                    <Icon name='crown' />
                    <Tooltip {...tooltipProps}>{t('Head of Household')}</Tooltip>
                  </span>
                )}
              </Photos.ContactProfilePhoto>
            </div>
          ) : (
            <Avatar name={personId ? text : undefined} {...avatarProps} size={avatarSize} />
          )}
        </AnimatePresence>
        {isCompactMode && (
          <Text
            css={{
              cursor: 'pointer',
              lineHeight: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            {...(textProps as TextProps)}
            onClick={(e) => {
              if (personId && openContactPanel) {
                setPersonId(personId, true, groupId);
              }
              textProps?.onClick?.(e);
            }}
            textAlign={variant === 'small' ? 'center' : undefined}
          >
            <Text as='span' css={{ fontWeight: theme.font.weight.bold, textTransform: 'capitalize' }}>
              {avatarProps?.firstName || text}
            </Text>
            {` ${avatarProps?.lastName || ''}`}
          </Text>
        )}
      </div>
      <div
        css={[
          {
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: variant === 'small' ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: variant === 'small' ? 'center' : 'start',
            flexGrow: 1,
            columnGap: theme.spacing(1),
          },
          styles.truncate,
        ]}
      >
        <div
          css={[
            !!subtext && {
              display: 'flex',
              flexDirection: 'column',
            },
          ]}
        >
          {!isCompactMode &&
            (textProps?.type === 'text' ? (
              <Text
                css={[
                  {
                    cursor: 'pointer',
                    lineHeight: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                ]}
                {...(textProps as TextProps)}
                onClick={(e) => {
                  if (personId && openContactPanel) {
                    setPersonId(personId, true, groupId);
                  }
                  textProps?.onClick?.(e);
                }}
                textAlign={variant === 'small' ? 'center' : undefined}
              >
                {text}
              </Text>
            ) : (
              <Heading
                css={[
                  {
                    cursor: openContactPanel ? 'pointer' : 'default',
                    lineHeight: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                ]}
                level={2}
                {...(textProps as HeadingProps)}
                onClick={(e) => {
                  if (personId && openContactPanel) {
                    setPersonId(personId, true, groupId);
                  }
                  textProps?.onClick?.(e);
                }}
                textAlign={variant === 'small' ? 'center' : undefined}
              >
                {text}
              </Heading>
            ))}
          {!!subtext && (
            <Text color='subdued' size='small' textAlign='left'>
              {subtext}
            </Text>
          )}
        </div>
        {children}
      </div>
    </Component>
  );
};

const householdBadgeStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute' as const,
  top: '-3px',
  right: '-7px',
  height: '20px',
  width: '20px',
  backgroundColor: theme.colors.warning10,
  borderRadius: theme.borderRadius.full,
  boxShadow: theme.shadows.light,
  color: theme.colors.warning60,
};

const householdBadgeSmallStyles = {
  top: 0,
  right: 0,
  height: '10px',
  width: '10px',
};
